import type { ApiWire } from '@/domain/shared/types';
import type {
    ClarifyCatalogDto,
    FetchHistoryResultDto,
    TestPenResultDto,
    TestProviderConfigResultDto,
} from '@/services/types';

import {
    CATALOG_REASONS,
    CATALOG_STATUSES,
    type ClarifyCatalogAnswer,
    type ClarifyCatalogInput,
    type FetchHistoryResult,
    TEST_PEN_REASONS,
    TEST_STATUSES,
    type TestPenReason,
    type TestPenResult,
    type TestProviderConfigInput,
    type TestResult,
    type TestStatus,
} from './actionTypes';
import { fieldPatches, secretsPatchToDto } from './fieldValues';
import { siteParts } from './mappers';
import type { ClarifyCatalogRequestWire, TestProviderConfigRequestWire } from './secretRequests';

/** A pen's site-scope keys in `fields` are its per-pen connection overrides (R16). */
export function toTestRequest(input: TestProviderConfigInput): TestProviderConfigRequestWire {
    return {
        ...siteParts(input),
        pens: input.pens.map((pen) => {
            const secrets = secretsPatchToDto(pen.secrets);
            return {
                cageId: pen.cageId,
                fields: fieldPatches(pen.fields),
                ...(secrets && { secrets }),
            };
        }),
    };
}

/** A status this build does not know reads as a failure rather than a pass. */
function testStatus(value: string): TestStatus {
    return TEST_STATUSES.find((status) => status === value) ?? 'error';
}

/** A reason this build does not know reads as none, which leaves the status to speak. */
export function testPenReason(value: string | null | undefined): TestPenReason | null {
    return TEST_PEN_REASONS.find((reason) => reason === value) ?? null;
}

function mapTestPen(dto: ApiWire<TestPenResultDto>): TestPenResult {
    return {
        cageId: dto.cageId,
        status: testStatus(dto.status),
        daysWithData: dto.daysWithData,
        signals: [...dto.signals],
        message: dto.message ?? null,
        reason: testPenReason(dto.reason),
        scada: dto.scada
            ? {
                  itemCount: dto.scada.itemCount,
                  found: dto.scada.found,
                  unknown: [...dto.scada.unknown],
                  unverified: dto.scada.unverified,
                  scadaOnly: dto.scada.scadaOnly,
              }
            : null,
    };
}

export function mapTestResult(dto: ApiWire<TestProviderConfigResultDto>): TestResult {
    return {
        from: dto.from,
        to: dto.to,
        results: dto.results.map(mapTestPen),
        testToken: dto.testToken ?? null,
    };
}

export function toCatalogRequest(input: ClarifyCatalogInput): ClarifyCatalogRequestWire {
    return siteParts(input);
}

export function mapCatalog(dto: ApiWire<ClarifyCatalogDto>): ClarifyCatalogAnswer {
    return {
        // As for a test: a status this build does not know reads as a failure, and an
        // unknown reason as none, which leaves the status to speak.
        status: CATALOG_STATUSES.find((status) => status === dto.status) ?? 'error',
        reason: CATALOG_REASONS.find((reason) => reason === dto.reason) ?? null,
        items: dto.items.map((item) => ({
            id: item.id,
            name: item.name,
            labels: [...item.labels],
            engUnit: item.engUnit ?? null,
        })),
        truncated: dto.truncated,
    };
}

export function mapFetchHistory(dto: ApiWire<FetchHistoryResultDto>): FetchHistoryResult {
    return {
        cageId: dto.cageId,
        from: dto.from,
        to: dto.to,
        providerName: dto.providerName ?? null,
        daysWithData: dto.daysWithData,
        written: dto.written,
        failedDays: [...dto.failedDays],
        partialOnly: dto.partialOnly,
        skipped: dto.skipped.map((entry) => ({
            providerName: entry.providerName,
            reason: entry.reason,
        })),
    };
}
